import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Tag } from "antd";
import { CalendarOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import UseApi from "../../Reusable/CallApi/UseApi";
import defaultCourseImg from "../../../assets/Icons/course-img.png";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .title {
    font-size: 20px;
    font-weight: 500;
    color: #1f243b;
  }

  .toolbar {
    display: flex;
    gap: 12px;
  }

  .search-input {
    width: 220px;
    border-radius: 20px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }

  .card {
    border: 1px solid #eee;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
  }

  .card-cover {
    width: 100%;
    height: 160px;
    object-fit: cover;
    background: #f5f5f5;
  }

  .card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .card-title {
    font-size: 16px;
    font-weight: 500;
    color: #1f243b;
  }

  .card-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #68728a;
    font-size: 13px;
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
  }
`;

const STATUS_LABEL = {
  in_progress: "In Progress",
  completed: "Completed",
  not_started: "Yet to Start",
};

const STATUS_COLOR = {
  in_progress: "orange",
  completed: "green",
  not_started: "default",
};

// A real, separate module from Course List (per Figma - two distinct
// sidebar items, not tabs under one page). There's no separate learner
// login anywhere in this app - see learnerMenuItem's comment in
// Redux/systemSlice.js. viewingAsPeopleId (set via the "Viewing as"
// picker in the header) is whose real enrollments this shows.
const MyCourse = () => {
  const navigate = useNavigate();
  const clientId = useSelector((state) => state.authTracking.userInfo.clientId);
  const viewingAsPeopleId = useSelector((state) => state.systemTracking.viewingAsPeopleId);

  const { data, loading, request } = UseApi();
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(null);

  useEffect(() => {
    if (!clientId || !viewingAsPeopleId) return;
    request({
      endPoint: `/course/getmycourses/${clientId}?people_id=${viewingAsPeopleId}`,
      method: "get",
    }).catch(() => {});
  }, [clientId, viewingAsPeopleId]);

  const courses = data?.response || [];

  const filteredCourses = useMemo(() => {
    const value = search.toLowerCase().trim();
    return courses.filter((c) => {
      const matchesSearch = !value || (c.course_name || "").toLowerCase().includes(value);
      const matchesDate = !dateFilter || dayjs(c.enrolled_at).isSame(dateFilter, "day");
      return matchesSearch && matchesDate;
    });
  }, [courses, search, dateFilter]);

  if (!viewingAsPeopleId) {
    return (
      <Wrapper>
        <div className="title">My Course</div>
        <div>
          Pick who you're viewing as from the "Learner" dropdown in the header
          to see their real course enrollments.
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="title">My Course</div>
      <div className="toolbar">
        <Input
          className="search-input"
          placeholder="Search"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <DatePicker placeholder="Date" value={dateFilter} onChange={setDateFilter} allowClear />
      </div>
      <div className="grid">
        {filteredCourses.map((course) => (
          <div
            className="card"
            key={course.enrollment_id}
            onClick={() =>
              navigate("/course-player", {
                state: { courseId: course.course_id, enrollmentId: course.enrollment_id },
              })
            }
          >
            <img className="card-cover" src={course.course_profile || defaultCourseImg} alt="" />
            <div className="card-body">
              <div className="card-title">{course.course_name}</div>
              {course.conducted_by && (
                <div className="card-meta">
                  <UserOutlined /> {course.conducted_by}
                </div>
              )}
              <div className="card-meta">
                <CalendarOutlined /> Enrolled: {course.enrolled_at ? new Date(course.enrolled_at).toLocaleDateString() : "-"}
              </div>
              <div className="card-footer">
                <Tag color={STATUS_COLOR[course.status] || "default"}>
                  {STATUS_LABEL[course.status] || course.status}
                </Tag>
                <span>{course.progress != null ? `${course.progress}%` : ""}</span>
              </div>
            </div>
          </div>
        ))}
        {!loading && filteredCourses.length === 0 && <div>No courses yet.</div>}
      </div>
    </Wrapper>
  );
};

export default MyCourse;
